import React, { ReactElement, useEffect, useState } from 'react';
import { Row, Col, Button, Form, message, Space } from 'antd';
import { useParams } from 'react-router';
import CcxComponentProps from '../../../../core/CcxComponent';
import ProvisionService from '../../../../services/ProvisionService';
import Deployments from '../../../../types/Deployments';
import styles from './DataStoreSettings.module.less';
import DataStoreSettingsMaintenance from './DataStoreSettingsMaintenance';
import DataStoreSettingsNameChange from './DataStoreSettingsNameChange';
import DataStoreSettingsProxySqlAdminPassword from './DataStoreSettingsProxySqlAdminPassword';
import DeploymentsItem from '../../../../types/DeploymentsItem';
import { Divider } from 'antd';
import DataStoreSettingsBackupSchedule from './DataStoreSettingsBackupSchedule';
import useBackupSettings from '../../../../core/hooks/useBackupSettings';
import BackupService from '../../../../services/BackupService';

interface UrlProps {
    dataStoreUuid: string;
}

interface Props extends CcxComponentProps {
    dataStores?: Deployments;
    refresh?: Function;
}

function DataStoreSettings({
    dataStores,
    refresh,
    testId = 'DataStoreSettings',
}: Props): ReactElement {
    const [form] = Form.useForm();
    const DEFAULT_MAINTENANCE_DAY = 6;
    const DEFAULT_MAINTENANCE_TIME = 2;
    let { dataStoreUuid } = useParams<UrlProps>();
    const [dataStoreName, setDataStoreName] = useState<string | undefined>(
        undefined
    );
    const [maintenanceDayOfWeek, setMaintenanceDayOfWeek] = useState<
        number | undefined
    >(undefined);
    const [maintenanceStartHour, setMaintenanceStartHour] = useState<
        number | undefined
    >(undefined);

    const [newDataStoreName, setNewDataStoreName] = useState<
        string | undefined
    >(undefined);
    const [newDayOfWeek, setNewDayOfWeek] = useState<number | undefined>(
        undefined
    );
    const [newStartHour, setNewStartHour] = useState<number | undefined>(
        undefined
    );
    const [newEndHour, setNewEndHour] = useState<number | undefined>(undefined);
    const [newPassword, setNewPassword] = useState<string | undefined>(
        undefined
    );
    const [newPasswordConfirm, setNewPasswordConfirm] = useState<
        string | undefined
    >(undefined);
    const [saving, setSaving] = useState(false);
    const [dataStore, setDataStore] = useState<DeploymentsItem | null>(null);
    const [canSave, setCanSave] = useState(false);
    const [canSaveBackup, setCanSaveBackup] = useState(false);

    const [full, setFull] = useState<boolean | undefined>();
    const [incremental, setIncremental] = useState<boolean | undefined>();
    const [frequency, setFrequency] = useState<number | undefined>();
    const [time, setTime] = useState<number | undefined>();

    const [fullCron, setFullCron] = useState<string>('');
    const [incCron, setIncCron] = useState<string>('');

    const {
        backupSettings,
        refresh: refreshBackupSettings,
        loading,
    } = useBackupSettings(dataStoreUuid);

    useEffect(() => {
        if (backupSettings) {
            setFullCron(backupSettings.fullCron || '');
            setIncCron(backupSettings.incCron || '');
            setIncremental((backupSettings.incFrequency || 0) > 0);

            setFull((backupSettings.fullFrequency || 0) > 0);

            if (backupSettings.fullFrequency === 1) {
                setFrequency(backupSettings.fullFrequency);
            } else if (backupSettings.fullFrequency === 2) {
                setFrequency(backupSettings.fullFrequency);
                setTime(backupSettings.fullStartHour);
            }
        }
    }, [backupSettings]);

    useEffect(() => {
        if (dataStores) {
            setDataStore(dataStores?.getByUuid(dataStoreUuid));
        }
    }, [dataStores]);

    useEffect(() => {
        if (dataStore) {
            setDataStoreName(dataStore.getName());
            setMaintenanceDayOfWeek(dataStore.getMaintenanceDayOfWeek());
            setMaintenanceStartHour(dataStore.getMaintenanceStartHour());
            form.setFieldsValue({
                maintenanceStartHour: dataStore.getMaintenanceStartHour(),
            });
            form.setFieldsValue({
                maintenanceDayOfWeek: dataStore.getMaintenanceDayOfWeek(),
            });
        }
    }, [dataStore]);

    const saveDataStoreName = (name: string) => {
        if (name !== dataStoreName) setNewDataStoreName(name);
        setCanSave(true);
    };

    const saveDayOfWeek = (dayOfWeek: number) => {
        if (dayOfWeek) setNewDayOfWeek(dayOfWeek);
        setCanSave(true);
    };

    const saveTimeWindow = (startHour: number, endHour: number) => {
        if (startHour >= 0) {
            setNewStartHour(startHour);
            setNewEndHour(endHour);
        }
        setCanSave(true);
    };

    const handlePasswordChange = (password: string) => {
        setNewPassword(password);
        setCanSave(true);
    };

    const handlePasswordConfirmChange = (passwordConfirm: string) => {
        setNewPasswordConfirm(passwordConfirm);
        setCanSave(true);
    };

    const handleSaveClick = async () => {
        try {
            if (newDataStoreName) await form.validateFields(['dataStoreName']);
            if (newDayOfWeek)
                await form.validateFields(['maintenanceDayOfWeek']);
            if (newStartHour || newEndHour)
                await form.validateFields(['maintenanceStartHour']);
            if (newPassword || newPasswordConfirm)
                await form.validateFields(['password', 'confirmPassword']);
        } catch (e) {
            message.error(
                'One or more settings are invalid. Please double check and try again.'
            );
            return;
        }

        setSaving(true);
        if (canSave) {
            try {
                await ProvisionService.updateDeployment({
                    dataStoreUuid: dataStoreUuid,
                    dataStoreName: newDataStoreName?.trim(),
                    dayOfWeek:
                        newDayOfWeek ||
                        form.getFieldValue('maintenanceDayOfWeek'),
                    startHour:
                        newStartHour ||
                        form.getFieldValue('maintenanceStartHour'),
                    endHour:
                        newEndHour ||
                        (form.getFieldValue('maintenanceStartHour') + 2) % 24,
                    proxySqlAdminPassword:
                        newPassword === newPasswordConfirm
                            ? newPassword
                            : undefined,
                });
                refresh && (await refresh());
                message.success('Your datastore settings have been updated.');
                setCanSave(false);
                setSaving(false);
                setNewDataStoreName(undefined);
                setNewDayOfWeek(undefined);
                setNewStartHour(undefined);
                setNewEndHour(undefined);
                setNewPassword(undefined);
                setNewPasswordConfirm(undefined);
            } catch (e) {
                message.error(
                    'There was an error updating your datastore settings. Please try again.'
                );
                setSaving(false);
                return;
            }
        }

        if (canSaveBackup) {
            try {
                let settingsObj = {
                    full_frequency: full ? frequency : 0,
                    full_start_hour: frequency === 2 ? time : undefined,
                    inc_frequency: incremental ? 1 : 0,
                };

                await BackupService.updateBackupSettings(
                    dataStoreUuid,
                    settingsObj
                );
                refreshBackupSettings && (await refreshBackupSettings());
                message.success('Your backup settings have been updated.');
                setCanSaveBackup(false);
                setSaving(false);
            } catch (e) {
                message.error(
                    'There was an error updating your backup settings. Please try again.'
                );
                setSaving(false);
                return;
            }
        }
    };

    return (
        <div className={styles.DataStoreSettings} data-testid={testId}>
            <DataStoreSettingsNameChange
                dataStoreName={dataStoreName}
                form={form}
                onChange={saveDataStoreName}
            />
            <Divider />
            <DataStoreSettingsMaintenance
                form={form}
                maintenanceDayOfWeek={maintenanceDayOfWeek}
                maintenanceStartHour={maintenanceStartHour}
                onDayOfWeekChange={saveDayOfWeek}
                onTimeWindowChange={saveTimeWindow}
            />
            <Divider />

            {false && (
                <>
                    {dataStore && dataStore?.isProxySql() && (
                        <>
                            <DataStoreSettingsProxySqlAdminPassword
                                form={form}
                                onPasswordChange={handlePasswordChange}
                                onPasswordConfirmChange={
                                    handlePasswordConfirmChange
                                }
                            />
                            <Divider />
                        </>
                    )}
                </>
            )}

            <Row>
                <Col
                    className={styles.DataStoreSettingsButtonContainer}
                    span={24}
                >
                    <Button
                        data-testid={`${testId}FormNextButton`}
                        loading={saving}
                        onClick={handleSaveClick}
                        type="primary"
                        htmlType="button"
                        disabled={!(canSave || canSaveBackup)}
                    >
                        Save
                    </Button>
                </Col>
            </Row>
        </div>
    );
}

export default DataStoreSettings;
